import { IndicatorSeries, type IndicatorTemplate, type KLineData } from 'klinecharts';

interface AtrResult {
  atr?: number;
}

// ATR ("Average True Range") is listed in indicatorDefs.ts but was never
// actually registered — it isn't a klinecharts built-in and there was no
// custom indicators/atr.ts either, so chart.createIndicator('ATR', ...)
// silently returned null (console: "indicator not supported") and the app's
// resolvedId fallback (ind.paneId ?? returnedId ?? ind.name) then stored a
// phantom 'ATR' pane-map entry with no backing pane — the catalog row did
// nothing and the legend never rendered it.
//
// Implements TR = max(H-L, |H-C_prev|, |L-C_prev|); ATR = EMA_n(TR), matching
// the formula documented in indicatorDefs.ts and the EMA-seeding convention
// klinecharts' own built-ins (EMA/MACD/TRIX) use: seed with a simple average
// of the first n values, then switch to the recursive EMA update.
export const atr: IndicatorTemplate<AtrResult> = {
  name: 'ATR',
  shortName: 'ATR',
  series: IndicatorSeries.Normal,
  calcParams: [14],
  figures: [{ key: 'atr', title: 'ATR: ', type: 'line' }],
  calc: (dataList: KLineData[], indicator) => {
    const period = indicator.calcParams[0];
    let trSum = 0;
    let atrValue = 0;
    return dataList.map((kLineData, i) => {
      const prevClose = (dataList[i - 1] ?? kLineData).close;
      const tr = Math.max(
        kLineData.high - kLineData.low,
        Math.abs(kLineData.high - prevClose),
        Math.abs(kLineData.low - prevClose),
      );
      const row: AtrResult = {};
      if (i < period - 1) {
        trSum += tr;
        return row;
      }
      if (i === period - 1) {
        trSum += tr;
        atrValue = trSum / period;
      } else {
        atrValue = (2 * tr + (period - 1) * atrValue) / (period + 1);
      }
      row.atr = atrValue;
      return row;
    });
  },
};

export default atr;
